import axios, { AxiosInstance } from 'axios';
import { ServerRequest } from '../types/request.types';
import {
  ValidateCodeResponse,
  RedeemCodeResponse,
  Reseller,
  ResellerDetail,
  ImportCouponsResponse,
  CreateResellerRequest,
  UpdateResellerRequest,
  CouponSetDetail,
  PaginatedRedemptionsResponse
} from '../types/reseller.types';

export default class ResellerService {
  private httpClient: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.httpClient = client;
  }

  async validateCode(code: string, resellerName?: string): Promise<ServerRequest<ValidateCodeResponse>> {
    let url = `/Reseller/validate?code=${encodeURIComponent(code)}`;
    if (resellerName) {
      url += `&resellerName=${encodeURIComponent(resellerName)}`;
    }
    const response = await this.httpClient.get(url);
    return response.data;
  }

  async redeemCode(couponCode: string): Promise<ServerRequest<RedeemCodeResponse>> {
    const response = await this.httpClient.post('/Reseller/redeem', { couponCode });
    return response.data;
  }

  async getResellerByCode(code: string): Promise<ServerRequest<Reseller | null>> {
    const response = await this.httpClient.get(`/Reseller/by-code/${encodeURIComponent(code)}`);
    return response.data;
  }

  async getResellerByName(name: string): Promise<ServerRequest<Reseller | null>> {
    const axiosWithoutAuth = axios.create({
      baseURL: axios.defaults.baseURL
    });
    const response = await axiosWithoutAuth.get(`/Reseller/by-name/${encodeURIComponent(name)}`);
    return response.data;
  }

  async getResellers(): Promise<ServerRequest<Reseller[]>> {
    const response = await this.httpClient.get('/Admin/resellers');
    return response.data;
  }

  async getResellerDetail(id: number): Promise<ServerRequest<ResellerDetail>> {
    const response = await this.httpClient.get(`/Admin/resellers/${id}`);
    return response.data;
  }

  async createReseller(data: CreateResellerRequest): Promise<ServerRequest<Reseller>> {
    const response = await this.httpClient.post('/Admin/resellers', data);
    return response.data;
  }

  async updateReseller(id: number, data: UpdateResellerRequest): Promise<ServerRequest<Reseller>> {
    const response = await this.httpClient.put(`/Admin/resellers/${id}`, data);
    return response.data;
  }

  async deleteReseller(id: number): Promise<ServerRequest<void>> {
    const response = await this.httpClient.delete(`/Admin/resellers/${id}`);
    return response.data;
  }

  async importCoupons(
    resellerId: number,
    couponSetName: string,
    pricePerLicense: number,
    file: File,
    skipHeader = true
  ): Promise<ServerRequest<ImportCouponsResponse>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('resellerId', resellerId.toString());
    formData.append('couponSetName', couponSetName);
    formData.append('pricePerLicense', pricePerLicense.toString());
    formData.append('skipHeader', skipHeader.toString());

    const response = await this.httpClient.post('/Admin/import-coupons', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  async getCouponSetDetail(
    id: number,
    page = 1,
    pageSize = 50,
    status?: string,
    search?: string
  ): Promise<ServerRequest<CouponSetDetail>> {
    let url = `/Admin/coupon-sets/${id}?page=${page}&pageSize=${pageSize}`;
    if (status) url += `&status=${encodeURIComponent(status)}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    
    const response = await this.httpClient.get(url);
    return response.data;
  }

  async getResellerRedemptions(
    id: number,
    page = 1,
    pageSize = 20,
    search?: string,
    startDate?: string,
    endDate?: string
  ): Promise<ServerRequest<PaginatedRedemptionsResponse>> {
    let url = `/Admin/resellers/${id}/redemptions?page=${page}&pageSize=${pageSize}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;
    
    const response = await this.httpClient.get(url);
    return response.data;
  }

  async downloadMonthlyCsv(
    id: number,
    startDate: string,
    endDate: string
  ): Promise<Blob> {
    const response = await this.httpClient.get(
      `/Admin/resellers/${id}/redemptions/monthly-csv?startDate=${startDate}&endDate=${endDate}`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  }
}

export const resellerService = new ResellerService(axios);
